import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import { ExternalLink, User, Facebook, Instagram, Linkedin, Twitter, Youtube, Mail, Phone, Lock } from 'lucide-react';
import { toast } from 'sonner';
import TierPaymentModal from '@/components/TierPaymentModal';
import TruncatedText from '@/components/TruncatedText';
import { useTranslatedMap, TranslatableText } from '@/contexts/LanguageContext';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Spotify icon component
const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const ResourcesPage = ({ user }) => {
  const [resources, setResources] = useState([]);
  const tr = useTranslatedMap(resources.flatMap((r) => [r.title, r.bio]));
  const [loading, setLoading] = useState(true);
  const [membershipType, setMembershipType] = useState(user?.membership_tier || null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTierPaymentModal, setShowTierPaymentModal] = useState(false);

  useEffect(() => {
    fetchResources();
    fetchMembershipStatus();
  }, []);

  // Handle hash navigation after resources load
  useEffect(() => {
    if (!loading && resources.length > 0 && window.location.hash) {
      const elementId = window.location.hash.substring(1);
      const element = document.getElementById(elementId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          element.classList.add('ring-2', 'ring-cyan-400', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-cyan-400', 'ring-offset-2');
          }, 3000);
        }, 100);
      }
    }
  }, [loading, resources]);

  const fetchMembershipStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembershipType(response.data.membership_tier || response.data.membership_type);
    } catch (error) {
      console.error('Failed to fetch membership status');
    }
  };

  // Check if user is Bronze or basic Free (view-only mode for resources)
  // free_comp is a complimentary full-access tier (VIP) and should NOT be restricted
  // null means still loading — don't restrict until we know the actual tier
  const isViewOnly = membershipType !== null && (membershipType === 'free' || membershipType === 'bronze');

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20"><TranslatableText text="Loading resources..." /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <TranslatableText as="h1" className="text-4xl sm:text-5xl font-bold text-white mb-3" text="Resources/Guides" />
        <TranslatableText as="p" className="text-lg text-gray-300" text="Meet our practitioners and explore helpful resources" />
      </div>

      {/* Membership Required Banner for Bronze Users */}
      {isViewOnly && (
        <Card 
          className="mb-6 border-2 border-yellow-500 bg-yellow-50 cursor-pointer hover:border-yellow-600 transition-all"
          onClick={() => setShowTierPaymentModal(true)}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">Limited Access - Click to Upgrade</h3>
                <p className="text-sm text-yellow-800">
                  Upgrade to Silver, Gold, or Platinum to unlock full practitioner contact information and exclusive resources.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {resources.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {resources.map((resource) => (
            <Card 
              key={resource.id} 
              id={`resource-${resource.id}`}
              data-testid={`resource-${resource.id}`} 
              className={`hover-lift border-2 relative ${isViewOnly ? 'border-gray-300' : 'border-gray-200 hover:border-cyan-400'} scroll-mt-24`}
            >
              {/* Overlay for Bronze users on contact info */}
              {isViewOnly && (
                <div className="absolute top-0 right-0 m-4 z-10">
                  <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    <span>Limited Access</span>
                  </div>
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-start gap-4">
                  <Avatar className="w-20 h-20">
                    <AvatarImage 
                      src={resource.photo_url} 
                      alt={resource.name} 
                    />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-purple-400 text-white text-2xl">
                      <User className="w-10 h-10" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-1">{resource.name}</CardTitle>
                    <p className="text-cyan-600 font-medium">{tr(resource.title)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Phone Number */}
                {!isViewOnly && resource.phone && (
                  <div className="flex items-center gap-2 mb-3 text-gray-700">
                    <Phone className="w-4 h-4 text-cyan-600" />
                    <a href={`tel:${resource.phone}`} className="hover:text-cyan-600 transition-colors">
                      {resource.phone}
                    </a>
                  </div>
                )}
                
                <p className="text-gray-700 mb-4 leading-relaxed whitespace-pre-wrap">
                  <TruncatedText text={tr(resource.bio)} maxLength={200} />
                </p>
                
                {/* Social Media Links - Hidden for view-only users */}
                {!isViewOnly && (resource.facebook || resource.instagram || resource.linkedin || resource.twitter || resource.youtube || resource.tiktok || resource.spotify || resource.email) && (
                  <div className="flex gap-2 mb-4 flex-wrap">
                    {resource.facebook && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        onClick={() => window.open(resource.facebook, '_blank')}
                      >
                        <Facebook className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.instagram && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-pink-500 text-pink-600 hover:bg-pink-50"
                        onClick={() => window.open(resource.instagram, '_blank')}
                      >
                        <Instagram className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.linkedin && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-700 text-blue-700 hover:bg-blue-50"
                        onClick={() => window.open(resource.linkedin, '_blank')}
                      >
                        <Linkedin className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.twitter && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-sky-500 text-sky-600 hover:bg-sky-50"
                        onClick={() => window.open(resource.twitter, '_blank')}
                      >
                        <Twitter className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.youtube && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500 text-red-600 hover:bg-red-50"
                        onClick={() => window.open(resource.youtube, '_blank')}
                      >
                        <Youtube className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.tiktok && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-black text-black hover:bg-gray-50"
                        onClick={() => window.open(resource.tiktok, '_blank')}
                      >
                        <TikTokIcon className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.spotify && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        onClick={() => window.open(resource.spotify, '_blank')}
                      >
                        <SpotifyIcon className="w-4 h-4" />
                      </Button>
                    )}
                    {resource.email && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-gray-500 text-gray-600 hover:bg-gray-50"
                        onClick={() => window.location.href = `mailto:${resource.email}`}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}

                {/* Main Links - Hidden for view-only users */}
                {!isViewOnly && (
                  <div className="space-y-2">
                    {resource.website && (
                      <Button 
                        variant="outline" 
                        className="w-full border-cyan-400 text-cyan-600 hover:bg-cyan-50"
                        onClick={() => window.open(resource.website, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Visit Website
                      </Button>
                    )}
                    
                    {resource.custom_link_1_url && (
                      <Button 
                        variant="outline" 
                        className="w-full border-gray-300 hover:bg-gray-50"
                        onClick={() => window.open(resource.custom_link_1_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.custom_link_1_label || 'Custom Link'}
                      </Button>
                    )}
                    
                    {resource.custom_link_2_url && (
                      <Button 
                        variant="outline" 
                        className="w-full border-gray-300 hover:bg-gray-50"
                        onClick={() => window.open(resource.custom_link_2_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.custom_link_2_label || 'Custom Link'}
                      </Button>
                    )}
                    
                    {resource.custom_link_3_url && (
                      <Button 
                        variant="outline" 
                        className="w-full border-gray-300 hover:bg-gray-50"
                        onClick={() => window.open(resource.custom_link_3_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.custom_link_3_label || 'Custom Link'}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No resources available yet. Check back soon!</p>
          </CardContent>
        </Card>
      )}

      <TierPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currentTier={user?.membership_tier || 'bronze'}
      />

      <TierPaymentModal
        isOpen={showTierPaymentModal}
        onClose={() => setShowTierPaymentModal(false)}
        onSuccess={() => {
          setShowTierPaymentModal(false);
          fetchResources();
          fetchMembershipStatus();
        }}
        currentTier={membershipType}
      />
    </div>
  );
};

export default ResourcesPage;